import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import './theme.css';
import { useAuth, defaultHandle } from './auth/useAuth';
import { db } from './firebase';
import { Backdrop } from './components/parts';

// Admin console: lazy-loaded so its chunk is only fetched when an admin opens
// it — players never download it. Reached via the in-game button or `#admin`.
const AdminPanel = lazy(() =>
  import('./admin/AdminPanel').then((m) => ({ default: m.AdminPanel })),
);
import { useGameSession } from './game/useGameSession';
import { Home } from './screens/Home';
import { Lobby } from './screens/Lobby';
import { RoleReveal } from './screens/RoleReveal';
import { Table } from './screens/Table';
import { GameOver } from './screens/GameOver';
import { Settings } from './screens/Settings';
import { setMusicMode } from './feedback';

const HANDLE_KEY = 'neonfall.handle';
const handleKey = (uid: string) => `${HANDLE_KEY}.${uid}`;

export default function App() {
  const auth = useAuth();
  // Rendered unconditionally, even before anonymous sign-in resolves, so the
  // brand/tagline/SEO copy on Home paints immediately instead of sitting
  // behind a loading screen — only the actions that need a uid (create/join)
  // wait on `ready`. Keyed on uid so a real identity swap (sign-out, which
  // re-anonymizes) resets local state; the pending→first-uid transition is
  // harmless since there's nothing to reset yet.
  return <SignedInApp key={auth.user?.uid ?? 'pending'} auth={auth} />;
}

function SignedInApp({ auth }: { auth: ReturnType<typeof useAuth> }) {
  // Prefer this account's saved callsign; fall back to a legacy global one (from
  // older builds), else the provider's name. A re-login must never overwrite a
  // callsign the player already chose.
  const mintedRef = useRef(false);
  const [handle, setHandle] = useState(() => {
    const uid = auth.user?.uid;
    const saved = (uid && localStorage.getItem(handleKey(uid))) || localStorage.getItem(HANDLE_KEY);
    if (saved) return saved;
    // No stored callsign — mint one (a random neon handle for guests) and pin it
    // to this uid so it stays put across reloads instead of re-rolling each time.
    mintedRef.current = true;
    const minted = defaultHandle(auth.user);
    if (uid) localStorage.setItem(handleKey(uid), minted);
    return minted;
  });
  // Local storage is per-browser: a cleared cache, private tab, or new device
  // has no record of a previously-chosen callsign, so the mint above falls back
  // to the sign-in provider's raw name. For a real (non-guest) account, recover
  // the last callsign this uid actually played under from its server record —
  // written on every createRoom/joinRoom — before that provider name sticks.
  useEffect(() => {
    const uid = auth.user?.uid;
    if (!uid || !mintedRef.current || auth.user?.isAnonymous) return;
    getDoc(doc(db, 'users', uid))
      .then((snap) => {
        const saved = snap.data()?.displayName as string | undefined;
        if (saved?.trim()) saveHandle(saved.trim().slice(0, 24));
      })
      .catch(() => {
        /* best-effort — the minted default stands */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.uid]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => location.hash === '#admin');

  const ready = auth.ready && !!auth.user;
  const session = useGameSession(auth.user?.uid ?? null, handle, auth.user?.photoURL ?? null);

  const musicMode = session.screen === 'table' && session.game?.pendingAttack ? 'duel' : 'ambient';
  useEffect(() => {
    setMusicMode(musicMode);
    return () => setMusicMode(null);
  }, [musicMode]);

  // Keep the overlay in sync with the URL hash so the browser back button closes
  // it (and a bookmarked #admin opens it).
  useEffect(() => {
    const onHash = () => setShowAdmin(location.hash === '#admin');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  function openAdmin() {
    setShowAdmin(true);
    if (location.hash !== '#admin') history.pushState(null, '', '#admin');
  }
  function closeAdmin() {
    setShowAdmin(false);
    if (location.hash === '#admin') history.replaceState(null, '', location.pathname + location.search);
  }

  // Once a uid exists: an invite link (?code=XXXXXX) beats resuming a previous
  // room. The query param survives the sign-in popup, so it still applies for
  // users who had to authenticate first.
  useEffect(() => {
    if (!ready) return;
    const code = new URLSearchParams(location.search).get('code')?.toUpperCase() ?? '';
    if (/^[A-Z0-9]{6}$/.test(code)) {
      history.replaceState(null, '', location.pathname); // consume the param
      session.joinRoom(code);
    } else {
      session.resumeIfPossible();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  function saveHandle(next: string) {
    setHandle(next);
    const uid = auth.user?.uid;
    if (uid) localStorage.setItem(handleKey(uid), next);
  }

  const appClass = [
    'app',
    session.screen === 'home' ? 'app-home' : '',
    session.screen === 'roleReveal' ? 'app-role-reveal' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <Backdrop />
      <div className={appClass} data-screen={session.screen}>
        {session.screen === 'home' && (
          <Home
            session={session}
            handle={handle}
            photoURL={auth.user?.photoURL ?? null}
            ready={ready}
            onOpenSettings={() => setShowSettings(true)}
            onOpenAdmin={auth.isAdmin ? openAdmin : undefined}
          />
        )}
        {session.screen === 'lobby' && <Lobby session={session} />}
        {session.screen === 'roleReveal' && <RoleReveal session={session} />}
        {session.screen === 'table' && <Table session={session} />}
        {session.screen === 'gameOver' && <GameOver session={session} />}
      </div>

      {showSettings && (
        <Settings
          user={auth.user}
          handle={handle}
          onSave={saveHandle}
          onLinkGoogle={() => auth.linkProvider('google')}
          linkBusy={auth.busy}
          linkError={auth.error}
          onSignOut={() => {
            setShowSettings(false);
            void session.leaveToHome();
            void auth.logOut();
          }}
          onClose={() => setShowSettings(false)}
        />
      )}

      {session.error && (
        <div className="toast" onClick={() => session.setError(null)}>
          {session.error}
        </div>
      )}

      {!showSettings && auth.error && <div className="toast">{auth.error}</div>}

      {showAdmin && (
        <Suspense fallback={null}>
          <AdminPanel onClose={closeAdmin} />
        </Suspense>
      )}
    </>
  );
}
